import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AddActivityDrawer from '../components/Common/AddActivityDrawer';
import SettingsPage from './SettingsPage';
import { useAuth } from '../hooks/useAuth';
import { activitiesAPI } from '../services/api';
import ActivityCard from '../components/Grid/ActivityCard';
import { DoCancelDialog } from '../components/Common/Dialog';
import { Button } from '../components/Common/Button';
import CloseModalButton from '../components/Common/CloseModalButton';
import { Cross } from '../components/Icons/CustomIcons';
import Loading from '../components/Common/Loading';


import { LogOut, Manual } from '../components/Icons/CustomIcons';

const ITEMS_PER_PAGE = 12;

const STATUS_FILTERS = [
  { key: 'all',      label: 'Alle' },
  { key: 'active',   label: 'Veröffentlicht' },
  { key: 'pending',  label: 'In Prüfung' },
  { key: 'archived', label: 'Archiviert' },
];

const MyActivities = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('drawer-open', showSettings);
    return () => document.body.classList.remove('drawer-open');
  }, [showSettings]);

  const loadActivities = useCallback(async () => {
    setIsFetching(true);
    try {
      const response = await activitiesAPI.getAll({ my_activities: true });
      const all = response.data;
      setTotalCount(all.length);
      setActivities(statusFilter === 'all' ? all : all.filter(a => a.status === statusFilter));
    } catch (err) {
      console.error('Fehler beim Laden der Aktivitäten:', err);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [statusFilter]);

  const handleActivityClick = (activity) => {
    navigate(`/aktivitaet-kontrolle/${activity.id}`);
  };

  const handleEdit = (activity) => {
    navigate(`/aktivitaet-kontrolle/${activity.id}?bearbeiten=1`);
  };

  const handleDelete = async () => {
    await loadActivities();
  };

  const addressLine = [
    user?.strasse,
    [user?.plz, user?.wohnort].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  // Load-more
  const paginated = activities.slice(0, visibleCount);
  const remaining = activities.length - visibleCount;
  const nextLoadCount = Math.min(ITEMS_PER_PAGE, remaining);

  return (
    <>
    <div className="my-activities-page">
      {/* ── Profil-Header ─────────────────────────────────── */}
      <div className="my-activities-profile">
        {/* Name + Aktivitätsanzahl */}
        <div className="map-name-row">
          <div>
            <h2 className="headings Font-H0-A-120-D Colors-Black">
              {[user?.vorname, user?.name].filter(Boolean).join(' ') || user?.username}
            </h2>
            <p className="body-m map-activity-count">
              {totalCount} {totalCount === 1 ? 'Aktivität erstellt' : 'Aktivitäten erstellt'}
            </p>
          </div>
        </div>

        {/* Kontaktinfo: E-Mail + Adresse */}
        <div className="map-contact-row">
          {user?.email && (
            <div className="map-contact-item">
              <span className="map-contact-label">E-Mail-Adresse</span>
              <span className="map-contact-value">{user.email}</span>
            </div>
          )}
          {addressLine && (
            <div className="map-contact-item">
              <span className="map-contact-label">Adresse</span>
              <span className="map-contact-value">{addressLine}</span>
            </div>
          )}
        </div>

        {/* Action-Buttons: Profil bearbeiten + Ausloggen */}
        <div className="map-action-row">
          <Button variant="secondary" onClick={() => setShowSettings(true)} className="map-settings-btn">
            <Manual color="var(--Colors-Accent-01)" size={20} style={{ marginRight: '0.375rem', color: '#000' }} />
            Profil bearbeiten
          </Button>
          <Button variant="outline" onClick={() => setShowLogoutConfirm(true)} aria-label="Abmelden" title="Abmelden" className="map-logout-btn">
            <LogOut size={20} />
            <span className="map-logout-label">Abmelden</span>
          </Button>
        </div>
      </div>
    </div>

      {/* ── Aktivitäten ───────────────────────────────────── */}
      <div className="my-activities-content">
        {/* Section-Title */}
        <h2 className="my-activities-section-title">Von Ihnen erstellte Aktivitäten</h2>

        {/* Status-Filter (horizontal scrollbar on mobile) */}
        <div className="my-activities-filter-bar" role="group" aria-label="Status-Filter">
          {STATUS_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`my-activities-filter-btn${statusFilter === key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Aktivitäten-Grid */}
        {loading ? (
          <Loading />
        ) : paginated.length === 0 ? (
          <p className="my-activities-empty">
            {statusFilter === 'all'
              ? 'Sie haben noch keine Aktivitäten erstellt.'
              : `Keine Aktivitäten mit Status "${STATUS_FILTERS.find(f => f.key === statusFilter)?.label}".`}
          </p>
        ) : (
          <>
            {isFetching && (
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 'var(--spacing-1) var(--margin) var(--spacing-2)', textAlign: 'right', padding: '0 var(--spacing-4)' }}>
                Aktualisierung...
              </p>
            )}
            <div className="grid-view">
              {paginated.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  onActivityClick={handleActivityClick}
                  showStatusBanner
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {remaining > 0 && (
              <div className="load-more-container">
                <Button
                  className="load-more-button"
                  variant="outline"
                  onClick={() => setVisibleCount(prev => prev + ITEMS_PER_PAGE)}
                  style={{ padding: '8px 16px' }}
                >
                  Mehr laden ({nextLoadCount})
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Settings-Drawer ─────────────────────────────────── */}
      {showSettings && (
        <div
          className="drawer-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Profil bearbeiten"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="drawer-panel"
            onClick={(e) => e.stopPropagation()}
          >            
            <CloseModalButton
              className="drawer-close"
              onClick={() => setShowSettings(false)}
              ariaLabel="Schliessen"
            >
              <Cross size={20} />
            </CloseModalButton>

            <SettingsPage onClose={() => setShowSettings(false)} />
          </div>
        </div>
      )}

      {/* ── Logout-Bestätigungsdialog ─────────────────────── */}
      {showLogoutConfirm && (
        <DoCancelDialog
          icon={LogOut}
          title="Abmelden?"
          text="Abmelden"
          message="Sie werden von Ihrem Konto abgemeldet. Sie können sich jederzeit wieder anmelden."
          onConfirm={logout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}

      {/* FAB + Drawer: Aktivität hinzufügen */}
      <AddActivityDrawer />
    </>
  );
};

export default MyActivities;